import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Share } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { enableReminders, disableReminders, pushSupported, isIOS, isStandalone, localTZ } from "@/lib/push";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const label = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

export function ReminderCard() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const needsInstall = isIOS() && !isStandalone();

  useEffect(() => { api.get("/reminders").then(({ data }) => setR(data)).catch(() => setR({ enabled: false, time: "20:00" })); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const toggle = (on) => run(async () => {
    on ? await enableReminders(r.time) : await disableReminders();
    setR({ ...r, enabled: on });
  }, on ? `Reminder set for ${label(r.time)} every day` : "Reminders turned off");
  const changeTime = (time) => {
    setR({ ...r, time });
    if (r.enabled) run(() => api.put("/reminders", { time, timezone: localTZ() }), `Reminder moved to ${label(time)}`);
  };

  if (!r) return null;
  return (
    <section className="card p-5 space-y-4 mt-4" data-testid="reminder-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-[#F5F2EC] grid place-items-center"><Bell className="h-[18px] w-[18px] text-[#C86D51]" strokeWidth={1.75} /></span>
          <div><div className="font-medium">Daily check-in reminder</div><div className="text-xs text-stone-500">A gentle notification. Skipped on days you've already checked in.</div></div>
        </div>
        <Switch className="tap" data-testid="reminder-switch" checked={r.enabled} disabled={busy || needsInstall || !pushSupported()} onCheckedChange={toggle} />
      </div>
      {needsInstall ? (
        <p className="text-xs text-stone-600 bg-[#F5F2EC] rounded-lg p-3 flex gap-2" data-testid="reminder-ios-hint">
          <Share className="h-4 w-4 shrink-0 text-[#C86D51]" />On iPhone, tap Share then "Add to Home Screen", open CAMRYN from your Home Screen, and turn reminders on here.
        </p>
      ) : !pushSupported() ? (
        <p className="text-xs text-stone-500" data-testid="reminder-unsupported">This browser doesn't support notifications. Try Chrome, Edge or Safari.</p>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-stone-600 flex items-center gap-2">Remind me at
            <select data-testid="reminder-time-select" value={r.time} onChange={(e) => changeTime(e.target.value)} disabled={busy}
              className="h-10 px-3 rounded-lg border border-stone-200 text-sm bg-white">
              {TIMES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </label>
          {r.enabled && <label className="flex items-center justify-between gap-3 w-full text-sm" data-testid="reminder-private-row">
            <span><span className="font-medium">Private notifications</span><span className="block text-xs text-stone-500">Keep plan details off your lock screen.</span></span>
            <Switch className="tap" data-testid="reminder-private-switch" checked={r.private !== false} disabled={busy}
              onCheckedChange={(v) => run(() => api.put("/reminders/privacy", { private: v }).then(() => setR((x) => ({ ...x, private: v }))), v ? "Lock-screen details hidden" : "Lock-screen details shown")} />
          </label>}
          {r.enabled && <button data-testid="reminder-test-btn" disabled={busy} onClick={() => run(() => api.post("/push/test"), "Test notification sent")}
            className="text-xs underline text-stone-600 hover:text-stone-900">Send a test</button>}
        </div>
      )}
    </section>
  );
}
